import React from 'react';
import Button from '@app/react-common/components/button/Button';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { AbTestFormData } from './abTestModalUtils';

interface AbTestModalPreviewTabProps {
  formData: AbTestFormData;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  shop: Shop | null;
  getPreviousTab: (currentTabName: string) => string | null;
  setActiveTab: (tab: string) => void;
  onClose: () => void;
}

export const AbTestModalPreviewTab: React.FC<AbTestModalPreviewTabProps> = ({
  formData,
  mode,
  shop,
  getPreviousTab,
  setActiveTab,
  onClose,
}) => {
  const handleBackClick = () => {
    const prevTab = getPreviousTab('Preview');
    if (prevTab) {
      setActiveTab(prevTab);
    }
  };

  // Generate variant letters (A, B, C, D...)
  const getVariantLetter = (index: number): string => {
    return String.fromCharCode(65 + index); // 65 is 'A' in ASCII
  };

  // Get the store name from shop
  const storeName = shop?.name?.replace('.myshopify.com', '') || 'storeName';

  // Filter selected variants
  const selectedVariants = formData.abTestVariants?.filter((v) => v.isSelected) || [];

  return (
    <>
      <div className="ab-test-modal-preview">
        <div className="ab-test-modal-preview__intro">
          <p className="ab-test-modal-preview__intro-text">
            Force a specific variant to preview it yourself, without relying on traffic allocation. Only available while
            the test is running.
          </p>
        </div>

        <div className="ab-test-modal-preview__instruction">
          <p className="ab-test-modal-preview__instruction-text">
            To preview a variant, add "
            <span className="ab-test-modal-preview__instruction-highlight">?pdqForcedVariant=</span>" followed by the
            variant letter to any page URL. For example:
          </p>
          <p className="ab-test-modal-preview__instruction-example">
            https://{storeName}.myshopify.com/products/example
            <span className="ab-test-modal-preview__instruction-param">?pdqForcedVariant=a</span>
          </p>
        </div>

        <div className="ab-test-modal-preview__variants">
          <p className="ab-test-modal-preview__variants-heading">Your variants</p>
          <div className="ab-test-modal-preview__variants-list">
            {selectedVariants.map((variant, index) => {
              const letter = getVariantLetter(index);
              const isControl = index === 0;

              return (
                <div key={variant.internalId} className="ab-test-modal-preview__variant">
                  <div className="ab-test-modal-preview__variant-info">
                    <div
                      className={`ab-test-modal-preview__variant-badge ${isControl ? 'ab-test-modal-preview__variant-badge--control' : ''}`}>
                      {letter}
                    </div>
                    <span className="ab-test-modal-preview__variant-name">
                      {variant.name} {isControl && '(Control)'}
                    </span>
                  </div>
                  <span className="ab-test-modal-preview__variant-param">?pdqForcedVariant={letter.toLowerCase()}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="ab-test-modal-preview__note">
          <p className="ab-test-modal-preview__note-text">
            If you've mapped checkout groups to this test, the matching checkout experience will also be applied. Use an
            incognito window for accurate testing.
          </p>
        </div>
      </div>

      <div className="ab-test-modal__actions">
        <Button
          onClick={handleBackClick}
          text={'Back'}
          size={'medium'}
          variant={'secondary'}
          className="ab-test-modal__actions__cancel"
        />
        <Button onClick={onClose} text={'Close'} size={'medium'} variant={'primary'} />
      </div>
    </>
  );
};
